import { type BlockConfig, iconField } from './_types';

/**
 * Payload `Block` config sidecar for the `articleList` block.
 *
 * Generic article-grid surface used by Webinars, Blog, BreachNews, etc.
 * Article rows themselves are NOT CMS-authored on this block -- the
 * consuming page (or, at cutover, a Payload `Posts` collection query)
 * supplies an `articles` array via a page-level prop. The CMS surface
 * here is the page chrome: title / search / filter tabs / empty state.
 *
 * Slug + interfaceName naming reconciliation:
 *   File name:               `article-list.tsx` / `article-list.payload.ts`
 *   React component export:  `ArticleList`
 *   Payload `slug`:          `articleList`
 *   Payload `interfaceName`: `ArticleListBlock`
 */

export const ArticleListBlock: BlockConfig = {
  slug: 'articleList',
  interfaceName: 'ArticleListBlock',
  labels: {
    singular: 'Article List',
    plural: 'Article List Blocks',
  },
  fields: [
    {
      name: 'eyebrow',
      type: 'text',
      label: 'Eyebrow',
    },
    {
      name: 'headline',
      type: 'text',
      label: 'Headline',
    },
    {
      name: 'subheadline',
      type: 'textarea',
      label: 'Subheadline',
      admin: { rows: 2 },
    },
    {
      name: 'category',
      type: 'text',
      label: 'Category (informational)',
      admin: {
        description:
          'Used at cutover by the Posts collection query (e.g. "blog", "breach", "webinar"). Not rendered.',
      },
    },
    {
      name: 'showSearch',
      type: 'checkbox',
      defaultValue: false,
      label: 'Show search input',
    },
    {
      name: 'columns',
      type: 'select',
      defaultValue: '3',
      label: 'Columns',
      options: [
        { label: '2', value: '2' },
        { label: '3', value: '3' },
        { label: '4', value: '4' },
      ],
    },
    {
      name: 'cardLayout',
      type: 'select',
      defaultValue: 'thumbnail',
      label: 'Card layout',
      admin: {
        description:
          'Three card variants: thumbnail (image + title + meta), text-only, and breach (BreachNews-specific severity-pill layout with affected-company + records + exposed-data chips + source/date footer). The breach layout consumes optional severity / affectedCompany / affectedRecords / exposedData / sourceName fields on each article -- those are runtime-only forwarded by the consuming page, not authored on this block.',
      },
      options: [
        { label: 'Thumbnail (image + title + meta)', value: 'thumbnail' },
        { label: 'Text only', value: 'text' },
        { label: 'Breach (severity pill + affected data)', value: 'breach' },
      ],
    },
    {
      name: 'cardCtaText',
      type: 'text',
      label: 'Per-card CTA text',
      defaultValue: 'Read More',
      admin: {
        description:
          'Defaults to "Read More". Webinars / video pages should set this to "Watch Now".',
      },
    },
    {
      name: 'linkBasePath',
      type: 'text',
      label: 'Link base path',
      admin: {
        description:
          'Prefix used for the article detail link (e.g. "/learn", "/webinars", "/breaches").',
      },
    },
    {
      name: 'filters',
      type: 'array',
      label: 'Filter tabs',
      labels: { singular: 'Filter', plural: 'Filters' },
      maxRows: 8,
      admin: {
        description:
          'Optional tab strip for category filtering. The first filter is selected by default. Use value "all" to render an unfiltered tab.',
      },
      fields: [
        {
          name: 'value',
          type: 'text',
          required: true,
          label: 'Filter value',
        },
        { name: 'label', type: 'text', required: true, label: 'Tab label' },
        iconField({
          name: 'icon',
          label: 'Icon (lucide-react name)',
        }),
      ],
    },
    {
      name: 'emptyState',
      type: 'group',
      label: 'Empty state',
      fields: [
        {
          name: 'headline',
          type: 'text',
          label: 'Headline',
          defaultValue: 'No articles found',
        },
        {
          name: 'body',
          type: 'text',
          label: 'Body',
          defaultValue: 'Check back soon for new content.',
        },
        iconField({
          name: 'icon',
          label: 'Icon (lucide-react name)',
        }),
      ],
    },
  ],
};

export default ArticleListBlock;
